import React, { useState, useEffect } from "react";
import { fetchUser } from "../../auth/auth";
import type { UserModel, UserParamsMap } from "../../type/user";
import UserInfoPanel from "./userInfoPanel";
import UserSettingsPanel from "./userSettingsPanel";
import UserListPanel from "./userListPanel";
import { fetchAllUsers } from "./userApi";
import "./userSettings.scss";
import PageHeader from "../../components/pageHeader/pageHeader";
import RegisterModal from "../../modal/registerModal/registerModal";
import { useSpinner } from "../../components/spinner/spinner";

const UserSettings: React.FC = () => {
  const [user, setUser] = useState<UserModel | null>(null);
  const [settings, setSettings] = useState<UserParamsMap>({});
  const [users, setUsers] = useState<UserModel[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserModel | null>(null);
  const [showRegister, setShowRegister] = useState(false);
  const {showSpinner, hideSpinner} = useSpinner();  

  // Replace the useEffect
  useEffect(() => {
    showSpinner();
    fetchUser().then((userData) => {
      if (userData) {
        setUser(userData);
        setSettings(userData.parameters || {});
        setSelectedUser(userData);
        if (userData.admin) {
          fetchAllUsers().then((all) => {
            setUsers(all);
          });
        }
      }
    }).finally(() => {
      hideSpinner();
    });
  }, []);

  const handleUserUpdate = (updatedUser: UserModel) => {
    // If updating the selected user, update state
    setSelectedUser((prev) => (prev && prev.iduser === updatedUser.iduser ? updatedUser : prev));
    // If updating self, update self
    setUser((prev) => (prev && prev.iduser === updatedUser.iduser ? updatedUser : prev));
    // Update user in users list
    setUsers((prev) => prev.map(u => u.iduser === updatedUser.iduser ? updatedUser : u));
  };

  const handleSettingsUpdate = (updatedSettings: UserParamsMap) => {
    setSettings(updatedSettings);
  };

  const handleUserSelect = (u: UserModel) => {
    // If clicking the logged-in user, always show their own details
    if (user && u.iduser === user.iduser) {
      setSelectedUser(user);
      setSettings(user.parameters || {});
    } else {
      setSelectedUser(u);
      setSettings(u.parameters || {});
    }
  };

  const isAdmin = user?.admin;

  return (
    <div className="user-settings-container">
        <PageHeader
          title="Settings"
          icon="bi-gear"
          addButton={
            isAdmin
              ? {
                  label: "Register User",
                  mobileLabel: "Register",
                  onClick: () => setShowRegister(true),
                }
              : undefined
          }
        />

      <div className="user-settings-panels">
        {/* User list panel for admins */}
        {isAdmin && (
          <UserListPanel
            users={users}
            selectedUserId={selectedUser?.iduser}
            onSelect={handleUserSelect}
          />
        )}

        {/* User data update panel */}
        <UserInfoPanel
          user={selectedUser}
          onUserUpdate={handleUserUpdate}
        />

        {/* Settings panel */}
        <UserSettingsPanel
          user={selectedUser}
          settings={settings}
          onSettingsUpdate={handleSettingsUpdate}
          onUserUpdate={handleUserUpdate}
        />
      </div>

      <RegisterModal isOpen={showRegister} onClose={() => setShowRegister(false)} />
    </div>
  );
};

export default UserSettings;